import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import api from "../services/api";
import LanguageSwitcher from "../components/LanguageSwitcher";

type Sport = { id: string; name: string; icon: string | null };

export default function RegisterClubAdmin() {
  const { t } = useTranslation();
  const [err, setErr] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [registrationComplete, setRegistrationComplete] = useState(false);
  const [sports, setSports] = useState<Sport[]>([]);
  const [sportsLoading, setSportsLoading] = useState(true);

  // Form state
  const [form, setForm] = useState({
    legal_name: "",
    short_name: "",
    sports: [] as string[],
    cui: "",
    cis_code: "",
    president_name: "",
    phone: "",
    website: "",
    email: "",
    street: "",
    city: "",
    county: "",
    country: "România",
    heard_about_us: "",
    terms_accepted: false,
    privacy_accepted: false,
  });

  /**
   * The ANAF check on the CUI.
   *
   * The form is gated on this: until the tax register confirms an active
   * company, there is nothing to register. It also fills in the fields it can,
   * so the club is not asked to retype what the state already knows.
   */
  const [anaf, setAnaf] = useState<{
    state: "idle" | "checking" | "ok" | "error";
    message: string;
    legalName: string;
  }>({ state: "idle", message: "", legalName: "" });

  // Validation state
  const [validation, setValidation] = useState({
    phone: { valid: true, message: "" },
    email: { valid: true, message: "" },
    website: { valid: true, message: "" },
  });

  const [submittedEmail, setSubmittedEmail] = useState("");
  const [sportsDropdownOpen, setSportsDropdownOpen] = useState(false);

  // Fetch available sports on mount
  useEffect(() => {
    api.get("/platform-sports/public")
      .then(({ data }) => setSports(data))
      .catch(() => setSports([]))
      .finally(() => setSportsLoading(false));
  }, []);

  // Close dropdown on outside click
  useEffect(() => {
    function handleClick(e: MouseEvent) {
      const target = e.target as HTMLElement;
      if (!target.closest("[data-sports-dropdown]")) {
        setSportsDropdownOpen(false);
      }
    }
    document.addEventListener("mousedown", handleClick);
    return () => document.removeEventListener("mousedown", handleClick);
  }, []);

  // Ask ANAF as soon as the code looks complete, debounced, with every answer
  // tagged by the code it was asked about so a slow reply cannot overwrite a
  // newer one.
  const lastCuiQuery = React.useRef("");
  useEffect(() => {
    const cui = form.cui.replace(/^ro/i, "").replace(/\D/g, "");
    lastCuiQuery.current = cui;

    if (cui.length < 2) {
      setAnaf({ state: "idle", message: "", legalName: "" });
      return;
    }

    setAnaf((prev) => ({ ...prev, state: "checking", message: "" }));
    const timer = setTimeout(async () => {
      try {
        const { data } = await api.get("/public/anaf/lookup", { params: { cui } });
        if (lastCuiQuery.current !== cui) return;

        if (data.already_registered) {
          setAnaf({
            state: "error",
            message: t(
              "register_club.cui_already",
              "Acest club este deja înregistrat pe iGROWth. Autentifică-te sau recuperează-ți parola."
            ),
            legalName: "",
          });
          return;
        }

        const c = data.company;
        setAnaf({ state: "ok", message: "", legalName: c.legal_name });
        // Fill in what ANAF knows, without overwriting what the applicant has
        // already typed for themselves.
        setForm((prev) => ({
          ...prev,
          legal_name: c.legal_name || prev.legal_name,
          short_name: prev.short_name || c.legal_name || "",
          city: prev.city || c.city || "",
          county: prev.county || c.county || "",
        }));
      } catch (e: any) {
        if (lastCuiQuery.current !== cui) return;
        setAnaf({
          state: "error",
          message:
            e?.response?.data?.error ||
            t("register_club.cui_failed", "Nu am putut verifica acest cod fiscal."),
          legalName: "",
        });
      }
    }, 500);

    return () => clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [form.cui]);

  function updateField(field: string, value: any) {
    setForm((prev) => ({ ...prev, [field]: value }));
  }

  function toggleSport(sportName: string) {
    setForm((prev) => ({
      ...prev,
      sports: prev.sports.includes(sportName)
        ? prev.sports.filter((s) => s !== sportName)
        : [...prev.sports, sportName],
    }));
  }

  // Real-time validation functions
  function validatePhone(value: string) {
    if (!value.trim()) {
      setValidation(prev => ({ ...prev, phone: { valid: true, message: "" } }));
      return;
    }
    // Romanian phone: starts with 0, 10 digits, or international format
    const phoneRegex = /^(\+?4?0?|0)[0-9]{9}$/;
    const cleanPhone = value.replace(/[\s\-().]/g, "");
    const valid = phoneRegex.test(cleanPhone);
    setValidation(prev => ({
      ...prev,
      phone: { valid, message: valid ? "" : t("register_club.invalid_phone", "Număr de telefon invalid") }
    }));
  }

  function validateEmail(value: string) {
    if (!value.trim()) {
      setValidation(prev => ({ ...prev, email: { valid: true, message: "" } }));
      return;
    }
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    const valid = emailRegex.test(value);
    setValidation(prev => ({
      ...prev,
      email: { valid, message: valid ? "" : t("register_club.invalid_email", "Adresă de email invalidă") }
    }));
  }

  function validateWebsite(value: string) {
    if (!value.trim()) {
      setValidation(prev => ({ ...prev, website: { valid: true, message: "" } }));
      return;
    }
    // Allow URLs with or without protocol
    const urlRegex = /^(https?:\/\/)?([\da-z\.-]+)\.([a-z\.]{2,6})([\/\w \.-]*)*\/?$/i;
    const valid = urlRegex.test(value);
    setValidation(prev => ({
      ...prev,
      website: { valid, message: valid ? "" : t("register_club.invalid_website", "Adresă web invalidă") }
    }));
  }

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    setErr(null);

    // Validations
    if (anaf.state !== "ok") {
      setErr(
        anaf.message ||
          t(
            "register_club.error_cui",
            "Începe cu codul fiscal (CUI/CIF) al clubului — îl verificăm la ANAF înainte de înregistrare."
          )
      );
      return;
    }
    if (!form.short_name.trim()) {
      setErr(t("register_club.error_short_name", "Numele scurt este obligatoriu"));
      return;
    }
    if (form.sports.length === 0) {
      setErr(t("register_club.error_sports", "Selectează cel puțin un sport"));
      return;
    }
    if (!form.president_name.trim()) {
      setErr(t("register_club.error_president", "Numele și prenumele președintelui sunt obligatorii"));
      return;
    }
    if (!form.phone.trim()) {
      setErr(t("register_club.error_phone", "Telefonul este obligatoriu"));
      return;
    }
    if (!form.email.trim()) {
      setErr(t("register_club.error_email", "Adresa de email este obligatorie"));
      return;
    }
    if (!form.terms_accepted) {
      setErr(t("register_club.error_terms", "Trebuie să accepți Termenii și Condițiile"));
      return;
    }
    if (!form.privacy_accepted) {
      setErr(t("register_club.error_privacy", "Trebuie să accepți Politica de Confidențialitate"));
      return;
    }

    setLoading(true);
    try {
      await api.post("/auth/register-club-admin", form);
      setSubmittedEmail(form.email);
      setRegistrationComplete(true);
    } catch (e: any) {
      setErr(e?.response?.data?.error || t("register_club.error_generic", "Înregistrarea a eșuat. Vă rugăm încercați din nou."));
    } finally {
      setLoading(false);
    }
  }

  // Success screen
  if (registrationComplete) {
    return (
      <div className="flex min-h-screen">
        <div className="relative hidden overflow-hidden lg:flex lg:w-1/2 bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900">
          <div className="absolute inset-0 opacity-10">
            <div className="absolute bg-blue-500 rounded-full top-20 left-20 w-72 h-72 filter blur-3xl"></div>
            <div className="absolute rounded-full bg-emerald-500 bottom-20 right-20 w-96 h-96 filter blur-3xl"></div>
          </div>
          <div className="relative z-10 flex flex-col items-center justify-center w-full p-12">
            <div className="text-center">
              <img src="/assets/ig_v_color.png" alt="iGrowth" className="w-56 h-auto mx-auto mb-4" />
              <p className="mb-8 text-xl text-slate-300">
                {t("auth.platform_tagline", "Sports Club Management Platform")}
              </p>
            </div>
          </div>
        </div>

        <div className="flex flex-col w-full lg:w-1/2 bg-slate-900">
          <div className="absolute top-4 right-4">
            <LanguageSwitcher />
          </div>
          <div className="flex items-center justify-center flex-1 p-8">
            <div className="w-full max-w-md text-center">
              <div className="mb-8 text-center lg:hidden">
                <img src="/assets/ig_h_white.png" alt="iGrowth" className="w-56 h-auto mx-auto mb-4" />
              </div>
              <div className="p-8 border shadow-xl bg-slate-800/50 backdrop-blur-sm rounded-2xl border-slate-700/50">
                <div className="flex items-center justify-center w-16 h-16 mx-auto mb-6 rounded-full bg-blue-500/20">
                  <svg className="w-8 h-8 text-blue-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 19v-8.93a2 2 0 01.89-1.664l7-4.666a2 2 0 012.22 0l7 4.666A2 2 0 0121 10.07V19M3 19a2 2 0 002 2h14a2 2 0 002-2M3 19l6.75-4.5M21 19l-6.75-4.5M3 10l6.75 4.5M21 10l-6.75 4.5m0 0l-1.14.76a2 2 0 01-2.22 0l-1.14-.76" />
                  </svg>
                </div>
                <h2 className="mb-4 text-2xl font-semibold text-white">
                  {t("register_club.check_email", "Verifică-ți emailul")}
                </h2>
                <p className="mb-6 text-slate-400">
                  {t("register_club.verification_sent", "Am trimis un link de verificare la adresa")}
                  <br />
                  <span className="font-medium text-blue-400">{submittedEmail}</span>
                </p>
                <p className="mb-6 text-sm text-slate-500">
                  {t("register_club.verification_instructions", "Apasă pe link-ul din email pentru a-ți activa contul. După verificare, vei putea să îți setezi parola.")}
                </p>
                <div className="p-4 mb-6 border rounded-lg bg-blue-500/10 border-blue-500/30">
                  <p className="text-sm text-blue-300">
                    {t("register_club.demo_notice", "Vei primi un cont Demo gratuit pentru 14 zile cu acces la toate funcționalitățile.")}
                  </p>
                </div>
                <Link
                  to="/login"
                  className="inline-block w-full px-4 py-3 font-medium text-white transition-all duration-200 bg-blue-600 rounded-lg hover:bg-blue-500"
                >
                  {t("auth.go_to_login", "Mergi la Login")}
                </Link>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Registration form
  return (
    <div className="flex h-screen overflow-hidden">
      {/* Left side - Branding */}
      <div className="relative flex-col justify-center hidden h-screen overflow-hidden bg-white lg:flex lg:w-1/2">
        {/* Background pattern */}
        <div className="absolute inset-0 opacity-10">
          <div className="absolute rounded-full top-20 left-20 w-72 h-72 bg-emerald-500 filter blur-3xl"></div>
          <div className="absolute bg-blue-500 rounded-full bottom-20 right-20 w-96 h-96 filter blur-3xl"></div>
        </div>

        <div className="flex items-center gap-4 justify-between mx-auto min-w-[410px]">
          <img src="/assets/cofinantare.png" alt="Confinantare Uniunea Europeana" className="h-12 mt-8 ml-8" />
          <img src="/assets/gov_ro.svg" alt="Guvernul Romaniei" className="h-12 mt-8 mr-8" />
        </div>

        <div className="relative z-10 flex flex-col items-center justify-center w-full h-full p-12">
          <div className="text-center">
            <img src="/assets/ig_v_color.png" alt="iGrowth" className="w-56 h-auto mx-auto mb-4" />
            <p className="mb-8 text-xl text-slate-700">
              {t("auth.platform_tagline", "Sports Club Management Platform")}
            </p>
            <div className="flex items-center gap-4 text-sm text-slate-600">
              <div className="flex items-center gap-3">
                <span className="flex items-center justify-center w-8 h-8 rounded-full bg-emerald-500">
                  <svg className="w-4 h-4 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                  </svg>
                </span>
                <span>{t("auth.feature_clubs", "Manage clubs & teams")}</span>
              </div>
              <div className="flex items-center gap-3">
                <span className="flex items-center justify-center w-8 h-8 rounded-full bg-emerald-500">
                  <svg className="w-4 h-4 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                  </svg>
                </span>
                <span>{t("auth.feature_training", "Track training sessions")}</span>
              </div>
              <div className="flex items-center gap-3">
                <span className="flex items-center justify-center w-8 h-8 rounded-full bg-emerald-500">
                  <svg className="w-4 h-4 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                  </svg>
                </span>
                <span>{t("auth.feature_gamification", "Gamification & rewards")}</span>
              </div>
            </div>
          </div>
        </div>

        <div className="p-6 text-xs font-semibold text-center text-slate-900">
          <p>Această platformă web și aplicație mobilă au fost realizate cu sprijinul financiar al Uniunii Europene prin Programul Educației și Ocupare 2021-2027, cofinanțat din FSE+.<br/>
          Conținutul acestui material nu reprezintă în mod obligatoriu poziția oficială a Uniunii Europene sau a Guvernului României.<br/>
          Pentru informații detaliate despre celalalte programe finanțate de Uniunea Europeană, vă rugăm să vizitați <a href="https://www.fonduri-ue.ro" target="_blank" rel="nofollow">www.fonduri-ue.ro</a></p>
        </div>
      </div>

      {/* Right side - Form (scrollable) */}
      <div className="flex flex-col w-full overflow-y-auto lg:w-3/5 bg-slate-900">
        <div className="fixed z-50 top-4 right-4">
          <LanguageSwitcher />
        </div>

        <div className="flex-1 p-6 pt-16 lg:p-8 lg:pt-8">
          <div className="w-full max-w-2xl mx-auto">
            <div className="mb-6 text-center lg:hidden">
              <img src="/assets/ig_h_white.png" alt="iGrowth" className="w-48 h-auto mx-auto mb-2" />
            </div>

            <div className="p-6 border shadow-xl lg:p-8 bg-slate-800/50 backdrop-blur-sm rounded-2xl border-slate-700/50">
              <h2 className="mb-2 text-2xl font-semibold text-white">
                {t("register_club.title", "Înregistrare Club")}
              </h2>
              <p className="mb-6 text-slate-400">
                {t("register_club.subtitle", "Completează datele clubului pentru a primi un cont Demo gratuit (14 zile)")}
              </p>

              {err && (
                <div className="p-3 mb-4 text-sm text-red-400 border rounded-lg bg-red-500/10 border-red-500/30">
                  {err}
                </div>
              )}

              <form onSubmit={handleSubmit} className="space-y-6">
                {/* Club Information Section */}
                <div>
                  <h3 className="flex items-center gap-2 mb-4 text-lg font-medium text-white">
                    <svg className="w-5 h-5 text-blue-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
                    </svg>
                    {t("register_club.section_club", "Informații Club")}
                  </h3>
                  <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                    {/* The first question, and the one everything else hangs
                        on: the fiscal code. We confirm it against the ANAF
                        register, fill in what the state already knows, and only
                        then let the rest of the form matter. */}
                    <div className="md:col-span-2">
                      <label className="block mb-1.5 text-sm font-medium text-slate-300" htmlFor="reg-cui">
                        {t("register_club.cui", "Cod fiscal (CUI/CIF)")} <span className="text-red-400">*</span>
                      </label>
                      <input
                        id="reg-cui"
                        type="text"
                        inputMode="numeric"
                        autoFocus
                        className={`w-full px-4 py-2.5 text-white transition-all border rounded-lg bg-slate-700/50 placeholder-slate-400 focus:outline-none focus:ring-2 focus:border-transparent ${
                          anaf.state === "ok"
                            ? "border-emerald-500 focus:ring-emerald-500"
                            : anaf.state === "error"
                            ? "border-red-500 focus:ring-red-500"
                            : "border-slate-600 focus:ring-blue-500"
                        }`}
                        placeholder={t("register_club.cui_placeholder", "ex: RO12345678")!}
                        value={form.cui}
                        onChange={(e) => updateField("cui", e.target.value)}
                        aria-describedby="reg-cui-status"
                        required
                      />
                      <div id="reg-cui-status" aria-live="polite">
                        {anaf.state === "checking" && (
                          <p className="mt-2 text-sm text-slate-400">
                            {t("register_club.cui_checking", "Verificăm la ANAF...")}
                          </p>
                        )}
                        {anaf.state === "error" && (
                          <p className="mt-2 text-sm text-red-400">{anaf.message}</p>
                        )}
                        {anaf.state === "ok" && (
                          <div className="flex items-start gap-3 p-3 mt-2 border rounded-lg bg-emerald-500/10 border-emerald-500/30">
                            <svg className="w-5 h-5 mt-0.5 text-emerald-400 shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden>
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M5 13l4 4L19 7" />
                            </svg>
                            <div className="min-w-0">
                              <p className="text-sm font-semibold text-emerald-300 break-words">{anaf.legalName}</p>
                              <p className="mt-0.5 text-xs text-emerald-400/80">
                                {t("register_club.cui_confirmed", "Datele oficiale au fost preluate de la ANAF.")}
                              </p>
                            </div>
                          </div>
                        )}
                        {anaf.state === "idle" && (
                          <p className="mt-2 text-xs text-slate-400">
                            {t(
                              "register_club.cui_hint",
                              "Îl verificăm în registrul ANAF și completăm singuri denumirea oficială."
                            )}
                          </p>
                        )}
                      </div>
                    </div>
                    <div>
                      <label className="block mb-1.5 text-sm font-medium text-slate-300">
                        {t("register_club.short_name", "Nume scurt")} <span className="text-red-400">*</span>
                      </label>
                      <input
                        type="text"
                        className="w-full px-4 py-2.5 text-white transition-all border rounded-lg bg-slate-700/50 border-slate-600 placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                        placeholder={t("register_club.short_name_placeholder", "ex: Club XYZ")!}
                        value={form.short_name}
                        onChange={(e) => updateField("short_name", e.target.value)}
                        required
                      />
                    </div>

                    {/* Sports multi-select dropdown */}
                    <div data-sports-dropdown className="relative">
                      <label className="block mb-1.5 text-sm font-medium text-slate-300">
                        {t("register_club.sports", "Sporturi")} <span className="text-red-400">*</span>
                      </label>
                      <button
                        type="button"
                        onClick={() => setSportsDropdownOpen(!sportsDropdownOpen)}
                        className="w-full px-4 py-2.5 text-left text-white transition-all border rounded-lg bg-slate-700/50 border-slate-600 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent flex items-center justify-between"
                      >
                        <span className={form.sports.length === 0 ? "text-slate-400" : "text-white"}>
                          {form.sports.length === 0
                            ? t("register_club.select_sports", "Selectează sporturi")
                            : form.sports.join(", ")}
                        </span>
                        <svg className={`w-4 h-4 text-slate-400 transition-transform ${sportsDropdownOpen ? "rotate-180" : ""}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                        </svg>
                      </button>
                      {sportsDropdownOpen && (
                        <div className="absolute z-50 w-full mt-1 overflow-y-auto border rounded-lg shadow-xl bg-slate-700 border-slate-600 max-h-48">
                          {sportsLoading ? (
                            <div className="px-4 py-3 text-sm text-slate-400">{t("common.loading", "Se încarcă...")}</div>
                          ) : sports.length === 0 ? (
                            <div className="px-4 py-3 text-sm text-slate-400">{t("register_club.no_sports", "Nu sunt sporturi disponibile")}</div>
                          ) : (
                            sports.map((sport) => (
                              <label
                                key={sport.id}
                                className="flex items-center gap-3 px-4 py-2.5 cursor-pointer hover:bg-slate-600/50 transition-colors"
                              >
                                <input
                                  type="checkbox"
                                  checked={form.sports.includes(sport.name)}
                                  onChange={() => toggleSport(sport.name)}
                                  className="w-4 h-4 text-blue-500 rounded border-slate-500 bg-slate-600 focus:ring-blue-500"
                                />
                                <span className="text-sm text-white">{sport.name}</span>
                              </label>
                            ))
                          )}
                        </div>
                      )}
                    </div>

                    <div>
                      <label className="block mb-1.5 text-sm font-medium text-slate-300">
                        {t("register_club.cis_code", "Cod CIS (Cod Identificare Sportivă)")}
                      </label>
                      <input
                        type="text"
                        className="w-full px-4 py-2.5 text-white transition-all border rounded-lg bg-slate-700/50 border-slate-600 placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                        placeholder={t("register_club.cis_placeholder", "ex: CIS-1234")!}
                        value={form.cis_code}
                        onChange={(e) => updateField("cis_code", e.target.value)}
                      />
                    </div>
                  </div>
                </div>

                {/* President / Contact Section */}
                <div>
                  <h3 className="flex items-center gap-2 mb-4 text-lg font-medium text-white">
                    <svg className="w-5 h-5 text-blue-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                    </svg>
                    {t("register_club.section_president", "Președinte / Persoană de contact")}
                  </h3>
                  <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                    <div>
                      <label className="block mb-1.5 text-sm font-medium text-slate-300">
                        {t("register_club.president_name", "Nume și prenume președinte")} <span className="text-red-400">*</span>
                      </label>
                      <input
                        type="text"
                        className="w-full px-4 py-2.5 text-white transition-all border rounded-lg bg-slate-700/50 border-slate-600 placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                        placeholder={t("register_club.president_placeholder", "ex: Ion Popescu")!}
                        value={form.president_name}
                        onChange={(e) => updateField("president_name", e.target.value)}
                        required
                      />
                    </div>
                    <div>
                      <label className="block mb-1.5 text-sm font-medium text-slate-300">
                        {t("register_club.phone", "Telefon")} <span className="text-red-400">*</span>
                      </label>
                      <input
                        type="tel"
                        className={`w-full px-4 py-2.5 text-white transition-all border rounded-lg bg-slate-700/50 placeholder-slate-400 focus:outline-none focus:ring-2 focus:border-transparent ${
                          !validation.phone.valid ? "border-red-500 focus:ring-red-500" : "border-slate-600 focus:ring-blue-500"
                        }`}
                        placeholder={t("register_club.phone_placeholder", "ex: 0722 123 456")!}
                        value={form.phone}
                        onChange={(e) => {
                          updateField("phone", e.target.value);
                          validatePhone(e.target.value);
                        }}
                        onBlur={(e) => validatePhone(e.target.value)}
                        required
                      />
                      {!validation.phone.valid && (
                        <p className="mt-1 text-xs text-red-400">{validation.phone.message}</p>
                      )}
                    </div>
                    <div>
                      <label className="block mb-1.5 text-sm font-medium text-slate-300">
                        {t("register_club.email", "Adresa de email")} <span className="text-red-400">*</span>
                      </label>
                      <input
                        type="email"
                        name="club-admin-email"
                        autoComplete="off"
                        className={`w-full px-4 py-2.5 text-white transition-all border rounded-lg bg-slate-700/50 placeholder-slate-400 focus:outline-none focus:ring-2 focus:border-transparent ${
                          !validation.email.valid ? "border-red-500 focus:ring-red-500" : "border-slate-600 focus:ring-blue-500"
                        }`}
                        placeholder={t("register_club.email_placeholder", "ex: contact@clubxyz.ro")!}
                        value={form.email}
                        onChange={(e) => {
                          updateField("email", e.target.value);
                          validateEmail(e.target.value);
                        }}
                        onBlur={(e) => validateEmail(e.target.value)}
                        required
                      />
                      {!validation.email.valid && (
                        <p className="mt-1 text-xs text-red-400">{validation.email.message}</p>
                      )}
                    </div>
                    <div>
                      <label className="block mb-1.5 text-sm font-medium text-slate-300">
                        {t("register_club.website", "Site web")}
                      </label>
                      <input
                        type="url"
                        className={`w-full px-4 py-2.5 text-white transition-all border rounded-lg bg-slate-700/50 placeholder-slate-400 focus:outline-none focus:ring-2 focus:border-transparent ${
                          !validation.website.valid ? "border-red-500 focus:ring-red-500" : "border-slate-600 focus:ring-blue-500"
                        }`}
                        placeholder={t("register_club.website_placeholder", "ex: https://clubxyz.ro")!}
                        value={form.website}
                        onChange={(e) => {
                          updateField("website", e.target.value);
                          validateWebsite(e.target.value);
                        }}
                        onBlur={(e) => validateWebsite(e.target.value)}
                      />
                      {!validation.website.valid && (
                        <p className="mt-1 text-xs text-red-400">{validation.website.message}</p>
                      )}
                    </div>
                  </div>
                </div>

                {/* Address Section */}
                <div>
                  <h3 className="flex items-center gap-2 mb-4 text-lg font-medium text-white">
                    <svg className="w-5 h-5 text-blue-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
                    </svg>
                    {t("register_club.section_address", "Adresa")}
                  </h3>
                  <div className="grid grid-cols-1 gap-4 md:grid-cols-4">
                    <div className="md:col-span-4">
                      <label className="block mb-1.5 text-sm font-medium text-slate-300">
                        {t("register_club.street", "Strada")}
                      </label>
                      <input
                        type="text"
                        className="w-full px-4 py-2.5 text-white transition-all border rounded-lg bg-slate-700/50 border-slate-600 placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                        placeholder={t("register_club.street_placeholder", "ex: Str. Sportului nr. 10")!}
                        value={form.street}
                        onChange={(e) => updateField("street", e.target.value)}
                      />
                    </div>
                    <div>
                      <label className="block mb-1.5 text-sm font-medium text-slate-300">
                        {t("register_club.city", "Oraș")}
                      </label>
                      <input
                        type="text"
                        className="w-full px-4 py-2.5 text-white transition-all border rounded-lg bg-slate-700/50 border-slate-600 placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                        placeholder={t("register_club.city_placeholder", "ex: București")!}
                        value={form.city}
                        onChange={(e) => updateField("city", e.target.value)}
                      />
                    </div>
                    <div>
                      <label className="block mb-1.5 text-sm font-medium text-slate-300">
                        {t("register_club.county", "Județ")}
                      </label>
                      <input
                        type="text"
                        className="w-full px-4 py-2.5 text-white transition-all border rounded-lg bg-slate-700/50 border-slate-600 placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                        placeholder={t("register_club.county_placeholder", "ex: Ilfov")!}
                        value={form.county}
                        onChange={(e) => updateField("county", e.target.value)}
                      />
                    </div>
                    <div className="md:col-span-2">
                      <label className="block mb-1.5 text-sm font-medium text-slate-300">
                        {t("register_club.country", "Țară")}
                      </label>
                      <select
                        className="w-full px-4 py-2.5 text-white transition-all border rounded-lg bg-slate-700/50 border-slate-600 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                        value={form.country}
                        onChange={(e) => updateField("country", e.target.value)}
                      >
                        <option value="România">România</option>
                        <option value="Moldova">Moldova</option>
                        <option value="Bulgaria">Bulgaria</option>
                        <option value="Ungaria">Ungaria</option>
                        <option value="Serbia">Serbia</option>
                        <option value="Ucraina">Ucraina</option>
                        <option value="Germania">Germania</option>
                        <option value="Austria">Austria</option>
                        <option value="Italia">Italia</option>
                        <option value="Spania">Spania</option>
                        <option value="Franța">Franța</option>
                        <option value="Marea Britanie">Marea Britanie</option>
                        <option value="Altă țară">{t("register_club.other_country", "Altă țară")}</option>
                      </select>
                    </div>
                  </div>
                </div>

                {/* Additional Info */}
                <div>
                  <label className="block mb-1.5 text-sm font-medium text-slate-300">
                    {t("register_club.heard_about_us", "Cum ai auzit de noi?")}
                  </label>
                  <select
                    className="w-full px-4 py-2.5 text-white transition-all border rounded-lg bg-slate-700/50 border-slate-600 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    value={form.heard_about_us}
                    onChange={(e) => updateField("heard_about_us", e.target.value)}
                  >
                    <option value="">{t("register_club.select_option", "Selectează o opțiune")}</option>
                    <option value="google">{t("register_club.heard_google", "Google / Motoare de căutare")}</option>
                    <option value="social_media">{t("register_club.heard_social", "Social Media (Facebook, Instagram, etc.)")}</option>
                    <option value="recommendation">{t("register_club.heard_recommendation", "Recomandare de la alt club")}</option>
                    <option value="event">{t("register_club.heard_event", "Eveniment sportiv")}</option>
                    <option value="federation">{t("register_club.heard_federation", "Federație sportivă")}</option>
                    <option value="other">{t("register_club.heard_other", "Altele")}</option>
                  </select>
                </div>

                {/* Terms and Privacy */}
                <div className="pt-2 space-y-3">
                  <label className="flex items-start gap-3 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={form.terms_accepted}
                      onChange={(e) => updateField("terms_accepted", e.target.checked)}
                      className="w-4 h-4 mt-1 text-blue-500 rounded border-slate-500 bg-slate-600 focus:ring-blue-500"
                    />
                    <span className="text-sm text-slate-300">
                      {t("register_club.accept_terms", "Accept")} <a href="/terms" target="_blank" className="text-blue-400 underline hover:text-blue-300">{t("register_club.terms_link", "Termenii și Condițiile")}</a> <span className="text-red-400">*</span>
                    </span>
                  </label>
                  <label className="flex items-start gap-3 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={form.privacy_accepted}
                      onChange={(e) => updateField("privacy_accepted", e.target.checked)}
                      className="w-4 h-4 mt-1 text-blue-500 rounded border-slate-500 bg-slate-600 focus:ring-blue-500"
                    />
                    <span className="text-sm text-slate-300">
                      {t("register_club.accept_privacy", "Accept")} <a href="/privacy" target="_blank" className="text-blue-400 underline hover:text-blue-300">{t("register_club.privacy_link", "Politica de Confidențialitate a Datelor")}</a> <span className="text-red-400">*</span>
                    </span>
                  </label>
                </div>

                {/* Submit Button */}
                <button
                  type="submit"
                  // Nothing to register until ANAF has confirmed an active
                  // company; the button says so rather than failing on submit.
                  disabled={loading || anaf.state !== "ok"}
                  className="w-full px-4 py-3 font-medium text-white transition-all duration-200 bg-blue-600 rounded-lg hover:bg-blue-500 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {loading
                    ? t("common.loading", "Se procesează...")
                    : t("register_club.submit", "Înregistrează Clubul")}
                </button>
              </form>

              <div className="mt-6 text-sm text-center text-slate-400">
                {t("auth.have_account", "Ai deja un cont?")}{" "}
                <Link to="/login" className="text-blue-400 transition-colors hover:text-blue-300">
                  {t("common.login", "Autentifică-te")}
                </Link>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
